import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ContentFilterPinterestView,
  ContentFilterPinterestViewProps,
  PinterestTypesEnum
} from './ContentFilterPinterest.view';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  CorePinterestBoardFieldsFragment,
  ImportSourceArgsGroupInput,
  PinterestSearchContentType,
  PinterestSearchSortType,
  PinterestSearchType,
  SocialOAuthProvider,
  SocialProfileFields_PinterestSocialProfile_Fragment
} from '../../../../models/graphql';
import { SearchPinterestPostVariables } from './ContentFilterPinterest.apollo';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { ApolloError } from '@apollo/client';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { useDidMount } from '../../../../hooks/useDidMount';
import { PostSourceType } from '../../../../constants/find-content';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { FormElementValueType } from '../../../../models/basic-objects';

export type ContentFilterPinterestControllerProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  searchParameters: SearchPinterestPostVariables | undefined;
  setSearchParameters: Dispatch<SetStateAction<SearchPinterestPostVariables | undefined>>;
  pinterestBoards: CorePinterestBoardFieldsFragment[];
  fetchPinterestPosts: (parameters: SearchPinterestPostVariables) => Promise<void>;
  fetchPinterestBoards: (profileId: string) => Promise<void>;
  purgePinterestCache: () => void;
};

export const ContentFilterPinterestController = (props: ContentFilterPinterestControllerProps): JSX.Element => {
  const {
    searchParameters,
    setSearchParameters,
    formInstance,
    setShowFilters,
    fetchPinterestPosts,
    purgePinterestCache
  } = props;
  const [showSaveSourceModal, setShowSaveSourceModal] = useState<boolean>(false);
  const { modalType, selectedMenuItem, setSelectedMenuItem } = useContext(TreeMenuContext);
  const [apolloErrorKey, setApolloErrorKey] = useState<string | undefined>(undefined);
  const { posts, setError, setSourceType, setSelectedPosts, profiles, setSearchStep, setHasSearched, onSearchResult } =
    useContext(FindContentContext);
  const [selectedAutoCompleteValue, setSelectedAutoCompleteValue] = useState<FormElementValueType>('');

  const typeValue = useWatch({ name: 'type', control: formInstance.control });

  const clearFilterBarForm = useCallback(() => {
    const elementNames = ['contentType', 'sortType', 'term'];
    elementNames.forEach(e => formInstance.resetField(e));

    setSearchStep(SearchStepEnum.ONE);
  }, [formInstance, setSearchStep]);

  const clearPinterestForm = useCallback(
    (excluded: string | string[]) => {
      const elementNames = [
        ...Object.values(PinterestSearchType),
        ...Object.values(PinterestTypesEnum),
        'boardsAutoComplete',
        'contentType',
        'sortType',
        'term'
      ];

      const excludedSet = new Set(Array.isArray(excluded) ? excluded : [excluded]);

      setSelectedAutoCompleteValue('');

      elementNames
        .filter(n => !excludedSet.has(n))
        .forEach(e => {
          formInstance.resetField(e);
        });
    },
    [formInstance]
  );

  const pinterestProfiles = useMemo(() => {
    return profiles.filter(
      p => p.__typename === 'PinterestSocialProfile'
    ) as SocialProfileFields_PinterestSocialProfile_Fragment[];
  }, [profiles]);

  const getPinterestPins = useCallback(
    (value: string, searchType: PinterestSearchType) => {
      purgePinterestCache();

      const profile =
        searchType === PinterestSearchType.PROFILE
          ? pinterestProfiles.find(p => p.id === value)
          : pinterestProfiles.find(p => p.boards.some(b => b.externalId === value));

      if (!profile) return;

      const externalId = searchType === PinterestSearchType.BOARD ? value : profile.pinterestId;

      const parameters: SearchPinterestPostVariables = {
        socialProfileId: profile.id,
        externalId: externalId,
        contentType: formInstance.getValues('contentType') ?? PinterestSearchContentType.ALL,
        sortType: formInstance.getValues('sortType') ?? PinterestSearchSortType.LATEST,
        searchType
      };

      setSearchParameters(parameters);

      fetchPinterestPosts(parameters)
        .then(() => {
          onSearchResult(parameters, pinterestProfiles, false);
          setShowFilters(true);
          setError(undefined);
        })
        .catch(error => {
          const apolloErrorType = getApolloErrorMessage(error);
          const remappedError = remapApolloErrors(error as ApolloError);

          setApolloErrorKey(apolloErrorType);
          setError(remappedError);
        });
    },
    [
      fetchPinterestPosts,
      formInstance,
      pinterestProfiles,
      purgePinterestCache,
      setError,
      setSearchParameters,
      setShowFilters,
      onSearchResult
    ]
  );

  // For now just manage data without fetch since boards are in profile
  // We already have boards for getting profiles
  const getPinterestBoards = useCallback(() => {
    purgePinterestCache();
    formInstance.setValue('boardsAutoComplete', '');
  }, [formInstance, purgePinterestCache]);

  const submitFormHandler = useCallback(
    (type: PinterestSearchType | PinterestTypesEnum, value: string) => {
      clearFilterBarForm();

      switch (type) {
        // Eventually should include Followed Boards
        case PinterestTypesEnum.PROFILE_BOARD:
          getPinterestBoards();
          break;
        case PinterestSearchType.PROFILE:
          getPinterestPins(value, PinterestSearchType.PROFILE);
          break;
        case PinterestTypesEnum.MY_PINS:
          getPinterestPins(value, PinterestSearchType.PROFILE);
          break;
        case PinterestTypesEnum.MY_BOARD:
          getPinterestPins(value, PinterestSearchType.BOARD);
          break;
        default:
          // If we fall through, don't show filters
          setShowFilters(false);
          break;
      }
    },
    [getPinterestBoards, getPinterestPins, setShowFilters, clearFilterBarForm]
  );

  const getPinterestInputArgs = useCallback((): ImportSourceArgsGroupInput => {
    const { type: searchType, contentType, sortType, term } = formInstance.getValues();
    const requiredArgs = {
      contentType,
      sortType,
      searchType,
      term
    };

    const thirdPartyProfileDisplayName = pinterestProfiles.find(
      p => p.id === searchParameters?.socialProfileId
    )?.displayName;

    const args = {
      ...requiredArgs,
      socialProfileId: searchParameters?.socialProfileId,
      // External ID is actually the PinterestID of the owner
      externalId: pinterestProfiles.find(p => p.id === searchParameters?.socialProfileId)?.pinterestId,
      // Board ID is only need or wanted if a board search
      boardId: searchType === PinterestSearchType.BOARD ? searchParameters?.externalId : undefined,
      thirdPartyProfileDisplayName
    };

    return { pinterest: args };
  }, [formInstance, pinterestProfiles, searchParameters]);

  const filterSearchHandler = useCallback(
    (resetFilterBar?: boolean) => {
      if (resetFilterBar) {
        clearFilterBarForm();
      }

      if (searchParameters) {
        purgePinterestCache();
        setError(undefined);

        setSearchStep(SearchStepEnum.TWO);

        const parameters: SearchPinterestPostVariables = {
          ...searchParameters,
          contentType: formInstance.getValues('contentType') ?? PinterestSearchContentType.ALL,
          sortType: formInstance.getValues('sortType') ?? PinterestSearchSortType.LATEST,
          term: formInstance.getValues('term') ?? ''
        };

        setSearchParameters(parameters);

        fetchPinterestPosts(parameters).catch(error => {
          const apolloErrorType = getApolloErrorMessage(error);
          const remappedError = remapApolloErrors(error as ApolloError);

          // If we error, make sure to clear cache
          purgePinterestCache();

          setApolloErrorKey(apolloErrorType);
          setError(remappedError);
        });
      }
    },
    [
      searchParameters,
      clearFilterBarForm,
      purgePinterestCache,
      setError,
      setSearchStep,
      formInstance,
      setSearchParameters,
      fetchPinterestPosts
    ]
  );

  const resetCallBackHandler = useCallback(() => {
    clearPinterestForm(['type', PinterestTypesEnum.PROFILE_BOARD]);
    setSelectedPosts([]);
    purgePinterestCache();
    setSearchStep(SearchStepEnum.ONE);
    setHasSearched(false);
    setShowFilters(false);
  }, [clearPinterestForm, purgePinterestCache, setHasSearched, setSearchStep, setSelectedPosts, setShowFilters]);

  // Prefill with SavedSource
  useEffect(() => {
    if (
      !modalType &&
      selectedMenuItem &&
      selectedMenuItem.__typename === 'ImportSource' &&
      selectedMenuItem.args.__typename === 'ImportSourceArgsPinterest'
    ) {
      purgePinterestCache();

      const { args } = selectedMenuItem;
      setError(undefined);

      formInstance.reset({
        ...args,
        platform: SocialOAuthProvider.PINTEREST,
        type: args.pinterestSearchType
      });

      const externalId =
        args.pinterestSearchType === PinterestSearchType.BOARD ? String(args.boardId) : String(args.externalId);

      const parameters: SearchPinterestPostVariables = {
        socialProfileId: String(args.socialProfileId),
        externalId,
        searchType: args.pinterestSearchType,
        contentType: args.pinterestContentType,
        sortType: args.pinterestSortType,
        term: args.term ?? ''
      };

      // Get the Pinterest Board name
      const profile = pinterestProfiles.find(p => p.id === String(args.socialProfileId));
      const boardName = profile?.boards.find(b => b.externalId === String(args.boardId))?.name || '';

      fetchPinterestPosts(parameters)
        .then(() => {
          setShowFilters(true);
          setSelectedMenuItem(undefined);
        })
        .catch(error => {
          setError(remapApolloErrors(error as ApolloError));
        })
        .finally(() => {
          setSearchParameters(parameters);
          onSearchResult(parameters, pinterestProfiles, false);
          formInstance.setValue(PinterestSearchType.PROFILE, String(args.socialProfileId));
          formInstance.setValue(PinterestTypesEnum.PROFILE_BOARD, String(args.socialProfileId));
          formInstance.setValue('contentType', String(args.pinterestContentType));
          formInstance.setValue('boardsAutoComplete', boardName);

          setSelectedAutoCompleteValue(String(args.boardId));
        });
    }
  }, [
    fetchPinterestPosts,
    formInstance,
    modalType,
    pinterestProfiles,
    purgePinterestCache,
    selectedMenuItem,
    setError,
    setSearchParameters,
    setSelectedMenuItem,
    setShowFilters,
    onSearchResult
  ]);

  // when form changes, reset form and filters
  useEffect(() => {
    setSearchParameters(undefined);
    clearPinterestForm(typeValue);
    setSelectedAutoCompleteValue(undefined);
    setSearchStep(SearchStepEnum.ONE);
    purgePinterestCache();
    setError(undefined);
    setShowFilters(false);
  }, [
    clearPinterestForm,
    purgePinterestCache,
    setError,
    setSearchParameters,
    setSearchStep,
    setShowFilters,
    typeValue
  ]);

  // Make sure form is cleared when unmounted
  useDidMount(() => {
    setError(undefined);
    setHasSearched(false);
    setSourceType(PostSourceType.PinterestSocialProfile);
    if (pinterestProfiles.length < 1) {
      formInstance.setValue('type', -1);
    } else {
      formInstance.setValue('type', PinterestSearchType.PROFILE);
    }
    return () => {
      clearPinterestForm('');
      setSourceType(undefined);
      setSelectedPosts([]);
      purgePinterestCache();
    };
  });

  const viewProps: ContentFilterPinterestViewProps = {
    ...props,
    submitFormHandler,
    filterSearchHandler,
    selectedAutoCompleteValue,
    setSelectedAutoCompleteValue,
    hasPosts: posts.length > 0,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getPinterestInputArgs,
    pinterestProfiles,
    apolloErrorKey,
    resetCallBackHandler
  };

  return <ContentFilterPinterestView {...viewProps} />;
};
